import { FormEvent, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiForgotPassword, apiResetPassword } from "../api";
import { AppearanceSelect } from "../components/AppearanceSelect";
import { AppBrandTagline } from "../components/AppLogo";
import { PasswordField } from "../components/PasswordField";

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") ?? "", [params]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (token) {
    async function onReset(e: FormEvent) {
      e.preventDefault();
      setError(null);
      setMessage(null);
      setBusy(true);
      try {
        await apiResetPassword(token, password);
        setMessage("Password updated. You can sign in now.");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Reset failed");
      } finally {
        setBusy(false);
      }
    }

    return (
      <div className="layout narrow">
        <div className="auth-appearance-row">
          <AppearanceSelect id="appearance-reset-token" />
        </div>
        <h1>Set new password</h1>
        <AppBrandTagline />
        <form className="card" onSubmit={onReset}>
          <PasswordField
            label="New password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            required
          />
          {error ? <p className="error">{error}</p> : null}
          {message ? <p className="success">{message}</p> : null}
          <button type="submit" disabled={busy}>
            {busy ? "Saving…" : "Update password"}
          </button>
        </form>
        <p className="muted">
          <Link to="/login">Back to sign in</Link>
        </p>
      </div>
    );
  }

  async function onForgot(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setBusy(true);
    try {
      await apiForgotPassword(email);
      setMessage("If an account exists for that email, a reset link was sent.");
    } catch {
      setError("Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="layout narrow">
      <div className="auth-appearance-row">
        <AppearanceSelect id="appearance-reset-forgot" />
      </div>
      <h1>Forgot password</h1>
      <AppBrandTagline />
      <p className="muted">We will email you a reset link if the account exists.</p>
      <form className="card" onSubmit={onForgot}>
        <label>
          Email
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            required
          />
        </label>
        {error ? <p className="error">{error}</p> : null}
        {message ? <p className="success">{message}</p> : null}
        <button type="submit" disabled={busy}>
          {busy ? "Sending…" : "Send reset link"}
        </button>
      </form>
      <p className="muted">
        <Link to="/login">Back to sign in</Link>
      </p>
    </div>
  );
}
